<section class="bg-star-gray-600 pt-[40px] pb-[60px] xl:pt-[64px] xl:pb-[100px]">
  <div class="container">
    <div class="flex flex-col gap-[40px] lg:flex-row lg:items-center lg:gap-[60px]">
      <div class="flex max-w-[720px] flex-col">
        <h1 class="text-[29px] leading-[1.25] text-white md:text-[40px] xl:text-[44px]">
          Online AML Check for Crypto Wallets and Transactions
        </h1>
        <p class="mt-[24px] text-[16px] leading-[1.8] text-[#C0C0C0] md:text-[18px]">
          An AML check answers one question before you accept crypto: where did these funds come
          from? AMLOfficer runs that check against four AML data providers at once — including
          BitOK, Global Ledger and Beosin — and brings the results back into a single dashboard,
          your Telegram group, or your own systems.
        </p>
        <div class="mt-[32px] flex flex-col gap-[16px] sm:flex-row sm:items-center">
          <a
            [href]="appLinks.createAccountHref()"
            target="_blank"
            class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
          >
            <span class="text-[16px] tracking-[0.3px]">Register Business Account</span>
            <mat-icon
              svgIcon="second-arrow-right"
              class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
            ></mat-icon>
          </a>
          <span class="text-[14px] leading-[1.6] text-white/50">
            Checks run inside the AMLOfficer application — registration is the first step.
          </span>
        </div>
      </div>
      <div class="flex justify-center lg:max-w-[380px]">
        <img
          src="./images/aml-providers.webp"
          alt=""
          width="342"
          height="197"
          class="w-full max-w-[342px] object-contain"
        />
      </div>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container flex flex-col gap-[40px] lg:flex-row lg:gap-[60px]">
    <div class="flex-1">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
        What an AML check is
      </h2>
      <p class="mt-[16px] text-[16px] leading-[1.8] text-[#C0C0C0]">
        An AML (anti-money laundering) check examines a crypto address or a transaction against
        blockchain analytics data and looks for connections to sanctioned entities, darknet markets,
        mixers, fraud schemes and stolen funds. What comes back is a risk picture of that address —
        where its funds came from and where they went — not a verdict about a person.
      </p>
      <p class="mt-[16px] text-[16px] leading-[1.8] text-[#C0C0C0]">
        Businesses run these checks for a practical reason: once the funds are on your books, their
        history becomes your problem. A check before a deposit, a settlement or a payout is the
        cheapest moment at which you can still say no.
      </p>
    </div>
    <div class="flex-1">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
        What AMLOfficer checks
      </h2>
      <ul class="mt-[16px] flex flex-col gap-[14px]">
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            <b class="font-semibold text-white">Wallets and addresses</b> — screen a counterparty
            before you trade, settle or pay out.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            <b class="font-semibold text-white">Deposits or withdrawals</b> — narrow the check to
            the direction of flow that matters for the decision in front of you.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            <b class="font-semibold text-white">Full transaction history</b> — when a counterparty
            deserves a deeper look than a single transfer.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            <b class="font-semibold text-white">Wallets you keep watching</b> — the same address can
            be added once and monitored continuously instead of re-checked by hand.
          </span>
        </li>
      </ul>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <div class="rounded-[10px] bg-[#25282D] px-[20px] py-[32px] lg:px-[40px] lg:py-[44px]">
      <h2 class="max-w-[820px] text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
        Four AML data providers behind one check
      </h2>
      <p class="mt-[16px] max-w-[820px] text-[16px] leading-[1.8] text-[#C0C0C0]">
        AMLOfficer aggregates four AML data providers, including BitOK, Global Ledger and Beosin.
        The point of aggregation is disagreement: providers draw on different datasets, and an
        address one of them calls clean is regularly flagged by another.
      </p>
      <p class="mt-[16px] max-w-[820px] text-[16px] leading-[1.8] text-[#C0C0C0]">
        A single-vendor check gives you one opinion about an address. Running the same address
        through several sources gives you the disagreements as well — and those are exactly the
        cases a compliance officer wants to see before approving anything. You get that from one
        account, one interface and one integration, instead of four separate contracts.
      </p>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      When businesses run AML checks
    </h2>
    <div class="mt-[24px] grid gap-[20px] md:grid-cols-2 xl:grid-cols-3">
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">OTC desks</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Large-volume trades where the source of a counterparty's funds is not visible. A check
          before settlement is the due diligence step that makes the trade defensible.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">VASPs and crypto exchangers</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Incoming assets can carry the history of earlier suspicious activity. Screening deposits
          keeps that history off your balance and out of your next audit.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Payment and finance teams</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Counterparties and asset flows are screened before they reach the books, so a blockchain
          entity that would disrupt operations is caught early.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Compliance officers</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Client and service data scattered across tools makes oversight slow. One place to run
          checks, keep the reports and hand them to whoever asks next.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Investigations and requests</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Regulatory and law-enforcement requests need a structured answer. A saved check with its
          data sources is a document you can attach, not a screenshot.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Before an integration</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Manual checks are also how teams test the data before wiring screening into their own
          product through the API.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      How to run an AML check with AMLOfficer
    </h2>
    <ol class="mt-[24px] flex flex-col gap-[20px]">
      <li class="flex items-start gap-[16px]">
        <span
          class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
          >1</span
        >
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          <b class="font-semibold text-white">Open a business account.</b> Registration is the entry
          point to the application — checks, reports and plan details live there, not on this page.
        </p>
      </li>
      <li class="flex items-start gap-[16px]">
        <span
          class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
          >2</span
        >
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          <b class="font-semibold text-white">Add the wallet or address.</b> Add wallets manually
          for one-off checks, or put them under automated monitoring straight away.
        </p>
      </li>
      <li class="flex items-start gap-[16px]">
        <span
          class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
          >3</span
        >
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          <b class="font-semibold text-white">Choose the AML insights you need.</b> Deposits,
          withdrawals or the full history — the scope of the check is yours to set.
        </p>
      </li>
      <li class="flex items-start gap-[16px]">
        <span
          class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
          >4</span
        >
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          <b class="font-semibold text-white">Read the report where you work.</b> Reports are
          available in the dashboard, and wallet compliance reports and alerts can be generated
          directly in Telegram.
        </p>
      </li>
      <li class="flex items-start gap-[16px]">
        <span
          class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
          >5</span
        >
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          <b class="font-semibold text-white">Bring in your team.</b> Invite colleagues, assign
          roles and keep investigations shared instead of sitting in one person's inbox.
        </p>
      </li>
    </ol>
    <p class="mt-[24px] max-w-[820px] text-[16px] leading-[1.8] text-[#C0C0C0]">
      If the decision happens inside your own product — a deposit screen, an onboarding step, a
      payout queue — the same checks are available through the
      <a routerLink="/aml-api" class="text-white underline underline-offset-4">AMLOfficer AML API</a
      >, and wallets you want to watch permanently belong in
      <a routerLink="/transaction-monitoring" class="text-white underline underline-offset-4"
        >crypto transaction monitoring</a
      >.
    </p>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      AML checks: common questions
    </h2>
    <div class="mt-[24px] grid gap-[24px] md:grid-cols-2">
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">What does an AML check show?</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          The risk profile of an address or transaction as the AML data providers see it: the
          sources its funds are connected to and the risk signals attached to them. It is a risk
          signal for your own decision, not legal advice and not a substitute for your compliance
          policy.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Why check through several providers?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Because they do not always agree. Providers draw on different datasets, and an address one
          of them calls clean is regularly flagged by another. AMLOfficer aggregates four of them so
          the disagreement reaches you instead of being lost.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Do I need an account to run a check?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Yes. Checks run inside the AMLOfficer application, so the first step is to open a business
          account. This page describes the service; it does not perform the check.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">What does an AML check cost?</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          AMLOfficer works on a pay-as-you-go model, and the terms depend on your volume, so there
          is no single public price. Your plan is shown in your account after registration.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Can I check an address more than once?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          You can re-run a check at any time, but repeated manual checks are usually a sign that the
          wallet belongs under continuous monitoring, where changes in its activity raise an alert
          on their own.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Can checks run inside our own system?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Yes — the same checks are available through the API, which is how teams embed screening in
          an existing onboarding, deposit or payout flow.
        </p>
      </div>
    </div>
  </div>
</section>

<app-business-account-cta
  heading="Start checking wallets with AMLOfficer"
  text="Open a business account to run AML checks across four data providers, invite your team and keep the reports in one place."
></app-business-account-cta>

<app-related-pages [pages]="relatedPages"></app-related-pages>
